/**
 * CONTRACT.md §2.4 — layout. Grids, stacks, rows, sections.
 *
 * `Push` is the one that looks odd and is not. In the mockups `.push` is a
 * class *on* an element (`<p class="push">August 2026</p>`); here it is an
 * element of its own, dropped between two children of a `Row`. It renders
 * identically — `margin-left:auto` on an empty flex item eats the free space —
 * and it means no primitive needs a `push` prop it would otherwise have to
 * carry for the two heads that use one. The two exceptions, where the contract's
 * own example puts `.push` on the note itself, are `CardNote` and
 * `RegionHead`/`SectionHead`'s `pushNote`.
 *
 * §5 refused `.span2`/`.span3`/`.span-all` and `.g5`: nothing spans, and five
 * equal columns has no legible rank. `Grid`'s `cols` union is that refusal.
 */

import { useRef, type ReactNode, type RefObject } from 'react'

import { useOverflowsX } from '../../shell/useOverflowsX.js'
import { cx, type Common } from './common.js'
import { Xs } from './Text.js'

const GRID_CLASS = {
  2: 'g2',
  3: 'g3',
  4: 'g4',
  '2-1': 'g-2-1',
  '3-2': 'g-3-2',
} as const

export type GridCols = keyof typeof GRID_CLASS

export type GridProps = Common & {
  readonly cols: GridCols
  /** `.grid--top` — `align-items:start`, for columns of different heights. */
  readonly top?: boolean | undefined
  readonly children: ReactNode
}

export function Grid({ cols, top, children, ...common }: GridProps) {
  return (
    <div className={cx('grid', GRID_CLASS[cols], top && 'grid--top')} {...common}>
      {children}
    </div>
  )
}

export type StackGap = 'tight' | 'default' | 'loose'

export type StackProps = Common & {
  /** `--sp-2` / `--sp-3` / `--sp-4`. Default is "inside a panel". */
  readonly gap?: StackGap | undefined
  readonly children: ReactNode
}

export function Stack({ gap, children, ...common }: StackProps) {
  return (
    <div
      className={cx('stack', gap === 'tight' && 'stack--tight', gap === 'loose' && 'stack--loose')}
      {...common}
    >
      {children}
    </div>
  )
}

export type LinesProps = Common & {
  /**
   * The smaller line **under** the value. Rendered as `.xs`, on its own line, always —
   * the caller hands over the content and never the element.
   */
  readonly under?: ReactNode | undefined
  readonly children: ReactNode
}

/**
 * A value and the smaller line under it. `.stack--tight` (§2.4) with the sub-line's
 * `.xs` supplied here rather than by the caller.
 *
 * **This exists because the same defect has now been found in three consecutive
 * rounds of review, on three different pages, and every time it was written the same
 * way:**
 *
 * ```tsx
 * <Td>{line.description}<Xs><Muted>{line.ref}</Muted></Xs></Td>
 * ```
 *
 * `.xs` is a font size and nothing else, so as a `<span>` that is two inline boxes
 * with no whitespace between them — JSX drops the newline the mockup's own HTML
 * relies on — and it prints `Business consulting — hoursREQ-115`, `€890$970`,
 * `Cancelledat period end, 31 May`, `$0.12pilot`. `Xs`'s own header already warned
 * about it and `Xs block` already fixed it; what kept failing is that both halves of
 * the pair were the call site's to get right, so getting one of them wrong stayed
 * possible. Here the pair is the primitive's: there is no arrangement of `Lines`
 * that renders the two on one line.
 *
 * The children are **not** wrapped: contiguous inline content becomes a single
 * anonymous flex item, so a value that is a bare string, a `<B>` or a `<Meter>` is
 * one line either way — and a wrapper would break `.stack > .meter`'s own
 * double-margin rule, which is the reason the burn cell needs it.
 */
export function Lines({ under, children, ...common }: LinesProps) {
  return (
    <Stack gap="tight" {...common}>
      {children}
      {under === undefined || under === null ? null : <Xs block>{under}</Xs>}
    </Stack>
  )
}

export type RowProps = Common & {
  /** `.row--top` — `align-items:flex-start`, when one child is taller. */
  readonly top?: boolean | undefined
  readonly children: ReactNode
}

export function Row({ top, children, ...common }: RowProps) {
  return (
    <div className={cx('row', top && 'row--top')} {...common}>
      {children}
    </div>
  )
}

/** Eats the free space in a `Row`, pushing everything after it to the right. */
export function Push() {
  return <span className="push" aria-hidden="true" />
}

export type SectionProps = Common & { readonly children: ReactNode }

/** The between-sections rhythm, `--sp-6`. `:first-child` loses its top margin. */
export function Section({ children, ...common }: SectionProps) {
  return (
    <section className="section" {...common}>
      {children}
    </section>
  )
}

export type SectionHeadProps = Common & {
  readonly title: ReactNode
  readonly note?: ReactNode | undefined
  /** `.push` on the note — "What you paid … August 2026". */
  readonly pushNote?: boolean | undefined
}

export function SectionHead({ title, note, pushNote, ...common }: SectionHeadProps) {
  return (
    <div className="section__head" {...common}>
      <h2>{title}</h2>
      {note === undefined ? null : <p className={pushNote === true ? 'push' : undefined}>{note}</p>}
    </div>
  )
}

/** What a scroller says when there is more of it than the reader can see. */
export const SCROLLX_NOTE = 'Wider than the screen — scroll sideways →'

export type ScrollNoteProps = Common & { readonly children?: ReactNode }

/**
 * The line under a sideways scroller, in words.
 *
 * R3-18. At 1024px the studio's clients table is 1187px inside 1009px and the
 * money run's is 1081px inside its column; at 390px `/studio`'s scroller is 640px
 * inside 293px. Nothing said so — while the nav one screen away prints "9 pages —
 * scroll sideways →" and the task board has always printed "6 client columns —
 * scroll sideways →". A table that is cut off at the right edge with no scrollbar
 * (every one of these is `overflow-x: auto`, and a trackpad draws none until it is
 * used) reads as a table with fewer columns, which is worse than a scrollbar: it
 * is a wrong answer rather than a missing one.
 *
 * **Words rather than a fade**, and the argument is `Board.tsx`'s, unchanged: a
 * gradient over the last column says "something is cut off" without saying that
 * the something is more of this table, and it cannot be read out, printed or
 * translated.
 *
 * The sentence names the fact rather than a count, which is where it differs from
 * the other two. The nav knows it rendered nine destinations and the board knows
 * it has six clients; a scroller is handed a `Table` or a `Calendar` as children
 * and can count nothing without reading into them, and a number a primitive
 * guessed at would be worse than no number.
 */
export function ScrollNote({ children, ...common }: ScrollNoteProps) {
  /*
   * `aria-hidden`, for the reason `Sidebar`'s nav note carries it: this is about
   * a *visual* affordance. A screen reader walks the whole table whatever the
   * viewport is doing, and the scroller is a focusable region, so "scroll
   * sideways" is an instruction its reader never has to follow. Every cell it is
   * pointing at is already announced.
   */
  return (
    <p className="scrollx-note" aria-hidden="true" {...common}>
      {children ?? SCROLLX_NOTE}
    </p>
  )
}

/**
 * `[ref, cue]` — the measurement and the note, for the two elements that carry
 * `.scrollx`.
 *
 * There are exactly two and there is one hook, because the alternative is the
 * mistake this fixes: three pages had added the affordance by hand and twenty-six
 * had not. `ScrollX` is one of the two; `CardBody scrollX` is the other, and it
 * cannot delegate to the first — `.card__body--tight > .table` is a child
 * combinator, so a wrapper between them loses the table's edge padding and its
 * 640px floor (`Card.tsx` says so at length).
 *
 * `watch` is `children`. `useOverflowsX` observes the *element*, and a scroller's
 * own border box never moves when the table inside it grows: the box is the
 * column, and the content is what got wider. A row arriving after a query lands
 * is a new render with new children and nothing else, which is precisely the
 * event a `ResizeObserver` cannot see.
 *
 * In jsdom every measurement is `0`, so the cue renders in no page test and no
 * recorded payload changes shape. The tests that need it true say so.
 */
function useScrollCue(
  children: ReactNode,
): readonly [RefObject<HTMLDivElement | null>, ReactNode, boolean] {
  const ref = useRef<HTMLDivElement>(null)
  const overflows = useOverflowsX(ref, children)
  return [ref, overflows ? <ScrollNote /> : null, overflows]
}

export type ScrollXProps = Common & { readonly children: ReactNode }

/**
 * The horizontal scroller a wide `Table` or `Calendar` gets on a narrow screen.
 * Below 640px its child is floored at 640px; above it, at nothing.
 */
export function ScrollX({ children, ...common }: ScrollXProps) {
  const [ref, cue, overflows] = useScrollCue(children)
  // `tabIndex={0}`: a wide table on a narrow viewport makes this genuinely
  // scrollable, and a `<div>` with no focusable descendant and no way to reach
  // it by keyboard fails WCAG 2.1.1 — axe's `scrollable-region-focusable`, which
  // only fires once a real viewport makes the overflow real (`a11y.spec.ts`'s
  // `mobile-journeys` project is what caught it; jsdom's page tests never do).
  return (
    /*
     * `.scrollx--fits` while the content is inside the box, and it is not decoration.
     *
     * `overflow-x: auto` makes this element a **scroll container in both axes** (a
     * `visible` on the other axis computes to `auto`), and a `position: sticky` cell
     * inside a scroll container sticks to *that* box rather than to the window — so
     * wrapping the Tasks table for the phone's sake silently turned off the sticky
     * table head everybody else was getting. Where the table fits there is nothing to
     * scroll, so the wrapper stops being a scrollport and the head sticks again;
     * where it does not, the sideways scroller wins, which is the right way round —
     * a page that slides under the reader is worse than a head that scrolls away.
     */
    <div
      className={cx('scrollx', !overflows && 'scrollx--fits')}
      tabIndex={0}
      ref={ref}
      {...common}
    >
      {children}
      {cue}
    </div>
  )
}

/** `CardBody scrollX`'s half of the cue. `Card.tsx` is the only other caller. */
export function useCardScrollCue(
  children: ReactNode,
  on: boolean,
): readonly [RefObject<HTMLDivElement | null> | undefined, ReactNode] {
  const [ref, cue] = useScrollCue(children)
  return on ? [ref, cue] : [undefined, null]
}
